/*
Copyright 2022-2024 New Vector Ltd.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

import { type FC, type ReactElement, type ReactNode, useEffect } from "react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import * as Sentry from "@sentry/react";
import { logger } from "matrix-js-sdk/lib/logger";
import { ErrorSolidIcon } from "@vector-im/compound-design-tokens/assets/web/icons";

import { Header, HeaderLogo, LeftNav, RightNav } from "./Header";
import styles from "./FullScreenView.module.css";
import { useUrlParams } from "./UrlParams";
import { RichError } from "./RichError";
import { ErrorView } from "./ErrorView";
import { Starfield, StarfieldToggle } from "./scry/Starfield";
import { ScryWordmark } from "./scry/ScryShell";

interface FullScreenViewProps {
  className?: string;
  children: ReactNode;
}

export const FullScreenView: FC<FullScreenViewProps> = ({
  className,
  children,
}) => {
  const { header, isWidget } = useUrlParams();
  return (
    <div
      className={classNames(
        styles.page,
        { [styles.scryPage]: !isWidget },
        className,
      )}
    >
      {!isWidget && <Starfield />}
      {!isWidget && header === "standard" && (
        <header className={styles.scryHeader}>
          <ScryWordmark />
        </header>
      )}
      {isWidget && header === "standard" && (
        <Header>
          <LeftNav>
            <HeaderLogo />
          </LeftNav>
          <RightNav />
        </Header>
      )}
      <div className={styles.container}>
        <div className={styles.content} data-scry-reveal>
          {children}
        </div>
      </div>
      {!isWidget && (
        <footer className={styles.scryFooter}>
          Powered by <a className={styles.attribution} href="https://github.com/element-hq/element-call/tree/main/docs" target="_blank" rel="noreferrer">the elements</a> ·{" "}
          <a href="https://github.com/kenalba/scry/tree/signal20" target="_blank" rel="noreferrer">source</a>
          <StarfieldToggle separator />
        </footer>
      )}
    </div>
  );
};

interface ErrorPageProps {
  error: unknown;
}

// Due to this component being used as the crash fallback for Sentry, which has
// weird type requirements, we can't just give this a type of FC<ErrorPageProps>
export const ErrorPage = ({ error }: ErrorPageProps): ReactElement => {
  const { t } = useTranslation();
  useEffect(() => {
    logger.error(error);
    Sentry.captureException(error);
  }, [error]);

  return (
    <FullScreenView>
      {error instanceof RichError ? (
        error.richMessage
      ) : (
        <ErrorView
          Icon={ErrorSolidIcon}
          title={t("error.generic")}
          rageshake
          fatal
        >
          <p>{t("error.generic_description")}</p>
        </ErrorView>
      )}
    </FullScreenView>
  );
};

export const LoadingPage: FC = () => {
  const { t } = useTranslation(undefined, { useSuspense: false });
  return (
    <div className={styles.loadingPage}>
      <Starfield fast />
      <header>
        <ScryWordmark />
      </header>
      <div role="status" aria-live="polite">
        {t("common.loading", { defaultValue: "Loading…" })}
      </div>
      <footer>
        <StarfieldToggle />
      </footer>
    </div>
  );
};
